/**
 * `<caos-typing-indicator>` — says that somebody is composing a reply right now. (Catalog: Conversation › TypingIndicator — the line that says somebody is writing a reply.)
 *
 * The smallest piece of the conversation set, and the only one whose whole job is to be TEMPORARY:
 * it stands at the foot of a stream while somebody types and is gone when their message arrives. It
 * draws no message and holds no state — a surface shows it and removes it.
 *
 * `who` names the person; with none it says somebody is typing without claiming who, which is what a
 * surface that knows a reply is coming but not from whom should say rather than guessing.
 *
 * ── The announcement, which is the part that is easy to get wrong ─────────────────────────────
 * It is a `role="status"` live region, so a screen reader is told without losing the reader's place.
 * The animated dots are `aria-hidden` and carry no information the sentence does not, and they stop
 * entirely under `prefers-reduced-motion: reduce` — an indicator that animates forever is the one
 * animation on a page a person cannot escape by finishing what they were doing.
 *
 * Styling seams: `::part(indicator)`, `::part(dots)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

const sheet = makeSheet(`
  :host { display: block; }

  .indicator {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: var(--caos-space-1) var(--caos-space-3);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: var(--caos-text-row);
  }

  .dots { display: inline-flex; gap: 3px; }
  .dots span {
    width: 4px;
    height: 4px;
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-text-muted);
    animation: caos-typing 1.2s infinite ease-in-out;
  }
  .dots span:nth-child(2) { animation-delay: 0.15s; }
  .dots span:nth-child(3) { animation-delay: 0.3s; }

  @keyframes caos-typing {
    0%, 60%, 100% { opacity: 0.3; }
    30% { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    /* The sentence carries the whole message, so a still row of dots loses nothing. */
    .dots span { animation: none; opacity: 0.6; }
  }
`);

export class CaosTypingIndicator extends CaosElement {
  static get observedAttributes(): string[] {
    return ['who'];
  }

  private readonly indicator: HTMLDivElement;
  private readonly text: HTMLSpanElement;

  constructor() {
    super(sheet);
    this.indicator = document.createElement('div');
    this.indicator.className = 'indicator';
    this.indicator.setAttribute('part', 'indicator');
    // `status` rather than `alert`: somebody typing is not urgent, and an assertive region would
    // interrupt whatever the reader was being read at the moment the other person started.
    this.indicator.setAttribute('role', 'status');

    this.text = document.createElement('span');
    this.text.setAttribute('part', 'label');

    const dots = document.createElement('span');
    dots.className = 'dots';
    dots.setAttribute('part', 'dots');
    dots.setAttribute('aria-hidden', 'true');
    dots.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));

    this.indicator.append(this.text, dots);
    this.root.append(this.indicator);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** Who is typing. Unset, the sentence does not claim to know. */
  get who(): string {
    return this.getAttribute('who') ?? '';
  }
  set who(value: string) {
    this.setAttribute('who', value);
  }

  private render(): void {
    const who = this.who.trim();
    this.text.textContent = who ? `${who} is typing…` : 'Someone is typing…';
  }
}

define('caos-typing-indicator', CaosTypingIndicator);

declare global {
  interface HTMLElementTagNameMap {
    'caos-typing-indicator': CaosTypingIndicator;
  }
}
